import { Link } from "react-router-dom";
import { ArrowUpRight, Users, CalendarCheck, ReceiptText, Wallet, ShieldCheck, FileSpreadsheet, Landmark, Calculator } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";

const services = [
  { icon: Wallet, title: "End-to-end payroll processing", body: "Monthly salary computation with your exact structure — Basic, DA, HRA, allowances, custom components — deducted per Indian statute and disbursed by our team." },
  { icon: CalendarCheck, title: "Attendance & timesheet review", body: "Daily check-in/out, backfills, WFH and half-day support. HR sees a strict monthly grid; employees log through a private portal." },
  { icon: Landmark, title: "Statutory compliance", body: "EPF (12% of Basic + DA), ESI (0.75% employee share ≤ ₹21K gross), state-wise Professional Tax and LWF, TDS under New Regime FY 2025-26 with 87A rebate — all filed on time." },
  { icon: ReceiptText, title: "Itemised payslip generation", body: "Every payslip carries your logo, GSTIN and address. Line items are broken out — EPF, ESI, PT, LWF, TDS — with a signed Harguon Payroll credit footer." },
  { icon: Users, title: "Employee self-service portal", body: "Employees see their own attendance, request leave, and download every past payslip. Zero HR tickets for basic questions." },
  { icon: FileSpreadsheet, title: "Bulk import & CSV workflows", body: "Onboard 50 employees in a single paste. Same for attendance backfills — we take the spreadsheet your accountant sends and turn it into live records." },
  { icon: ShieldCheck, title: "Role-based access & audit", body: "Three tiers — service provider, HR admin, employee — enforced end-to-end. Every action leaves a trail." },
  { icon: Calculator, title: "Live salary calculator", body: "Model any offer or salary change before you commit. Every deduction shown in real time, per state and per opt-in." },
];

const process = [
  { n: "I", t: "Discovery call", d: "A partner reviews your headcount, salary structures and statutory footprint. We propose the engagement in writing." },
  { n: "II", t: "Onboarding week", d: "Employees are added by our team from your source of truth. Every salary structure is verified against offer letters." },
  { n: "III", t: "Monthly cycle", d: "Attendance closed → payroll run → itemised payslips issued → statutory returns filed. Every month, without a nudge." },
];

export default function ServicesPage() {
  return (
    <div className="min-h-screen bg-[#FAF7EF] text-neutral-950">
      <SiteNav />

      {/* Hero */}
      <section className="border-b border-[#E8DFC5] bg-grid relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{background: "radial-gradient(900px 500px at 80% 0%, rgba(201,169,97,0.15), transparent 60%)"}}></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 relative">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#8C6F1E] mb-6 flex items-center gap-3">
            <span className="inline-block w-8 h-px bg-[#C9A961]"></span>
            Services · what we do
          </div>
          <h1 className="font-serif font-black text-[52px] leading-[0.98] md:text-[80px] tracking-[-0.03em] max-w-4xl" data-testid="services-title">
            Your payroll office,<br />
            <span className="italic text-gold-gradient">delivered as a service.</span>
          </h1>
          <p className="mt-8 text-lg text-neutral-700 max-w-2xl leading-relaxed">
            HG Consultancy handles every moving part of payroll — from onboarding a new hire to filing the last statutory return of the year.
            You get one relationship manager, one monthly deliverable, and zero compliance anxiety.
          </p>
        </div>
      </section>

      {/* Service grid */}
      <section className="border-b border-[#E8DFC5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#8C6F1E] mb-3">§ 01 — Deliverables</div>
          <h2 className="font-serif font-black text-4xl md:text-5xl tracking-[-0.02em] mb-16 max-w-3xl leading-[0.95]">
            Eight services.<br />One retainer.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="services-grid">
            {services.map((s, i) => (
              <div key={s.title} className="group border border-[#E8DFC5] bg-white p-8 hover:border-[#C9A961] hover:shadow-[0_0_0_1px_rgba(201,169,97,0.4)] transition-all">
                <s.icon className="w-6 h-6 mb-6 text-[#8C6F1E]" strokeWidth={1.5} />
                <div className="font-serif font-bold text-xl tracking-tight mb-2">{s.title}</div>
                <p className="text-sm text-neutral-600 leading-relaxed">{s.body}</p>
                <div className="mt-6 text-[10px] font-mono uppercase tracking-widest text-neutral-400 group-hover:text-[#8C6F1E]">0{i + 1} / 0{services.length}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="border-b border-[#E8DFC5] bg-neutral-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{background: "radial-gradient(600px 300px at 20% 100%, rgba(201,169,97,0.25), transparent 60%)"}}></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 relative">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#D4B87D] mb-3">§ 02 — Engagement</div>
          <h2 className="font-serif font-black text-4xl md:text-5xl tracking-[-0.02em] mb-16 max-w-3xl leading-[0.95]">
            How we work,<br />
            <span className="italic text-gold-gradient">week by week.</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-px bg-[#2a2416]">
            {process.map((s) => (
              <div key={s.n} className="bg-neutral-950 p-10">
                <div className="font-serif italic text-[#C9A961] text-4xl mb-6">{s.n}</div>
                <div className="font-serif font-bold text-3xl tracking-tight mb-3">{s.t}</div>
                <p className="text-sm text-neutral-400 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-[#E8DFC5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#8C6F1E] mb-3">Ready?</div>
            <h3 className="font-serif font-black text-4xl md:text-5xl tracking-[-0.02em] leading-[0.95]">
              Get a proposal in <span className="italic text-gold-gradient">48 hours.</span>
            </h3>
          </div>
          <div className="flex flex-wrap gap-3 lg:justify-end">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-neutral-950 text-[#D4B87D] px-6 py-4 font-mono text-sm hover:bg-[#C9A961] hover:text-neutral-950 border border-neutral-950 hover:border-[#C9A961] transition-colors">
              Speak to a partner <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link to="/register" className="inline-flex items-center gap-2 border border-[#C9A961] px-6 py-4 font-mono text-sm hover:bg-neutral-950 hover:text-[#D4B87D] transition-colors">
              Open a workspace
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
